import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { Card, CardBody, CardHeader, CardTitle } from "@progress/kendo-react-layout";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import { extractProblemDetails } from "@presentation/common";
import { useInjection } from "@presentation/components/dependencyInjection";

const DEFAULT_BADGE_STYLE: React.CSSProperties = {
  marginLeft: "8px",
  padding: "2px 6px",
  borderRadius: "999px",
  backgroundColor: "#e0f2fe",
  color: "#075985",
  fontSize: "12px",
};

type VersionGroup<T> = {
  key: string;
  majorVersion: number;
  minorVersion?: number;
  versions: T[];
};

const parseVersion = (version: string) => {
  const match = /^(?:[A-Z]{2})?(\d+)\.(\d+)\./.exec(version);
  if (!match) {
    return null;
  }

  return { majorVersion: Number(match[1]), minorVersion: Number(match[2]) };
};

const sortGroupKeys = (a: string, b: string) => b.localeCompare(a, undefined, { numeric: true });

const groupCandidateVersions = (versions: string[]): VersionGroup<string>[] => {
  const groups = new Map<string, VersionGroup<string>>();

  versions.forEach((version) => {
    const parsed = parseVersion(version);
    const key = parsed ? `${parsed.majorVersion}.${parsed.minorVersion}` : "Other";
    const group = groups.get(key) ?? {
      key,
      majorVersion: parsed?.majorVersion ?? 0,
      minorVersion: parsed?.minorVersion,
      versions: [],
    };
    group.versions.push(version);
    groups.set(key, group);
  });

  return [...groups.values()]
    .sort((a, b) => sortGroupKeys(a.key, b.key))
    .map((group) => ({
      ...group,
      versions: group.versions.sort((a, b) => b.localeCompare(a, undefined, { numeric: true })),
    }));
};

const formatError = (error: unknown, fallback: string) => {
  const { title, detail, messages } = extractProblemDetails(error);
  return [title, detail, ...messages].filter((message): message is string => !!message?.trim()).join(" ") || fallback;
};

const versionButtonStyle = (selected: boolean): React.CSSProperties => selected
  ? { backgroundColor: "#dbeafe", borderColor: "#93c5fd", color: "#1e3a8a" }
  : { backgroundColor: "white" };

const SoftwareVersionsPage: React.FC = () => {
  const injectedVersionsService = useInjection(ListSoftwareVersionsService);
  const versionsService = useRef(injectedVersionsService).current;
  const [candidateVersions, setCandidateVersions] = useState<string[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<Set<string>>(new Set());
  const [defaultVersion, setDefaultVersion] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [minorVersionFilters, setMinorVersionFilters] = useState<Record<string, string>>({});

  const loadVersions = useCallback(async () => {
    setCandidatesLoading(true);
    setError("");

    await versionsService.getCandidateVersionsListAsync()
      .then(async (candidates) => {
        setCandidateVersions(candidates);
        const selected = await versionsService.getSelectedSpectronautVersionsAsync(candidates);
        const nextDefaultVersion = await versionsService.getDefaultSpectronautVersionAsync(candidates);
        setSelectedVersions(new Set(selected));
        setDefaultVersion(nextDefaultVersion ?? null);
      })
      .catch((loadError) => setError((currentError) => [currentError, formatError(loadError, "Failed to load candidate software versions.")].filter(Boolean).join(" ")))
      .finally(() => setCandidatesLoading(false));
  }, [versionsService]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const candidateVersionGroups = useMemo(() => groupCandidateVersions(candidateVersions), [candidateVersions]);
  const majorGroups = useMemo(() => {
    const groups = new Map<number, VersionGroup<string>[]>();
    candidateVersionGroups.filter((group) => group.key !== "Other").forEach((group) => {
      const majorGroups = groups.get(group.majorVersion) ?? [];
      majorGroups.push(group);
      groups.set(group.majorVersion, majorGroups);
    });

    return [...groups.entries()].sort(([majorA], [majorB]) => majorB - majorA);
  }, [candidateVersionGroups]);

  const persistSelectedVersions = (next: Set<string>) => {
    void versionsService.setSelectedSpectronautVersionsAsync(candidateVersions, [...next])
      .catch((saveError) => setError(formatError(saveError, "Failed to save selected Spectronaut versions.")));
  };

  const persistDefaultVersion = (version: string) => {
    setDefaultVersion(version);
    void versionsService.setDefaultSpectronautVersionAsync(version)
      .catch((saveError) => setError(formatError(saveError, "Failed to save the default Spectronaut version.")));
  };

  const toggleVersions = (versions: string[]) => {
    setSelectedVersions((current) => {
      const next = new Set(current);
      const allSelected = versions.every((version) => next.has(version));
      versions.forEach((version) => {
        if (allSelected) {
          next.delete(version);
        } else {
          next.add(version);
        }
      });
      persistSelectedVersions(next);
      if (!next.has(defaultVersion ?? "")) {
        setDefaultVersion(null);
      }
      return next;
    });
  };

  const toggleVersion = (version: string) => {
    setSelectedVersions((current) => {
      const next = new Set(current);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      persistSelectedVersions(next);
      if (!next.has(defaultVersion ?? "")) {
        setDefaultVersion(null);
      }
      return next;
    });
  };

  const setSelectedMinorGroups = (minorGroups: VersionGroup<string>[], selectedGroupKeys: string[]) => {
    setSelectedVersions((current) => {
      const next = new Set(current);
      minorGroups.forEach((group) => {
        const shouldSelectGroup = selectedGroupKeys.includes(group.key);
        group.versions.forEach((version) => {
          if (shouldSelectGroup) {
            next.add(version);
          } else {
            next.delete(version);
          }
        });
      });
      persistSelectedVersions(next);
      return next;
    });
  };

  const setMinorVersionFilter = (majorVersion: number, value: string) => {
    setMinorVersionFilters((current) => ({ ...current, [String(majorVersion)]: value }));
  };

  const allSelected = candidateVersions.length > 0 && candidateVersions.every((version) => selectedVersions.has(version));

  return (
    <div style={{ padding: "16px" }}>
      <Card>
        <CardHeader>
          <CardTitle>Spectronaut Versions</CardTitle>
        </CardHeader>
        <CardBody>
          <p style={{ marginTop: 0 }}>Select which Spectronaut image tags are active for job setup.</p>
          <p style={{ marginTop: 0, color: "#475569" }}>Right-click a version to mark it as the default.</p>

          {error && <div role="alert" style={{ marginBottom: "12px", color: "#b00020" }}>{error}</div>}

          {candidatesLoading ? (
            <p>Loading registry tags...</p>
          ) : candidateVersions.length === 0 ? (
            <p>No registry tags are available.</p>
          ) : (
            <div style={{ display: "grid", gap: "14px" }}>
              <div>
                <Button fillMode={allSelected ? "solid" : "outline"} style={versionButtonStyle(allSelected)} onClick={() => toggleVersions(candidateVersions)}>
                  All Spectronaut versions
                </Button>
              </div>

              {majorGroups.map(([majorVersion, minorGroups]) => {
                const majorVersions = minorGroups.flatMap((group) => group.versions);
                const majorSelected = majorVersions.every((version) => selectedVersions.has(version));
                const selectedMinorGroups = minorGroups.filter((group) => group.versions.every((version) => selectedVersions.has(version))).map((group) => group.key);
                const minorVersionFilter = minorVersionFilters[String(majorVersion)] ?? "";
                const visibleMinorGroups = minorGroups.filter((group) => group.key.toLowerCase().includes(minorVersionFilter.toLowerCase()));
                return (
                  <section key={majorVersion} style={{ border: "1px solid #d7dee8", borderRadius: "8px", padding: "12px" }}>
                    <div style={{ marginBottom: "12px" }}>
                      <Button fillMode={majorSelected ? "solid" : "outline"} style={versionButtonStyle(majorSelected)} onClick={() => toggleVersions(majorVersions)}>
                        Spectronaut {majorVersion}
                      </Button>
                    </div>

                    <div style={{ marginBottom: "12px", maxWidth: "420px" }}>
                      <MultiSelect
                        data={minorGroups.map((group) => group.key)}
                        value={selectedMinorGroups}
                        placeholder={`Search minor versions ...`}
                        filterable
                        filter={minorVersionFilter}
                        autoClose={false}
                        ariaLabel={`Minor versions for Spectronaut ${majorVersion}`}
                        popupSettings={{ height: "220px" }}
                        onFilterChange={(event: MultiSelectFilterChangeEvent) => setMinorVersionFilter(majorVersion, event.filter.value ?? "")}
                        onChange={(event: MultiSelectChangeEvent) => setSelectedMinorGroups(minorGroups, (event.value as string[]) ?? [])}
                      />
                    </div>

                    <div style={{ display: "grid", gap: "10px", paddingLeft: "18px" }}>
                      {visibleMinorGroups.map((group) => {
                        const minorSelected = group.versions.every((version) => selectedVersions.has(version));
                        return (
                          <section key={group.key} style={{ borderLeft: "2px solid #e1e5ea", paddingLeft: "12px" }}>
                            <div style={{ marginBottom: "8px" }}>
                              <Button fillMode={minorSelected ? "solid" : "outline"} style={versionButtonStyle(minorSelected)} onClick={() => toggleVersions(group.versions)}>
                                {group.key}
                              </Button>
                            </div>
                              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", paddingLeft: "18px" }}>
                                {group.versions.map((candidate) => (
                                  <Button
                                    key={candidate}
                                    fillMode={selectedVersions.has(candidate) ? "solid" : "outline"}
                                    style={versionButtonStyle(selectedVersions.has(candidate))}
                                    onClick={() => toggleVersion(candidate)}
                                    onContextMenu={(event) => {
                                      event.preventDefault();
                                      persistDefaultVersion(candidate);
                                      setSelectedVersions((current) => new Set(current).add(candidate));
                                    }}
                                  >
                                    {candidate}
                                    {defaultVersion === candidate && <span style={DEFAULT_BADGE_STYLE}>default</span>}
                                  </Button>
                                ))}
                              </div>
                          </section>
                        );
                      })}
                    </div>
                  </section>
                );
              })}

              {candidateVersionGroups.filter((group) => group.key === "Other").map((group) => (
                <section key={group.key} style={{ border: "1px solid #e1e5ea", borderRadius: "6px", padding: "10px 12px" }}>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {group.versions.map((candidate) => (
                      <Button
                        key={candidate}
                        fillMode={selectedVersions.has(candidate) ? "solid" : "outline"}
                        style={versionButtonStyle(selectedVersions.has(candidate))}
                        onClick={() => toggleVersion(candidate)}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          persistDefaultVersion(candidate);
                          setSelectedVersions((current) => new Set(current).add(candidate));
                        }}
                      >
                        {candidate}
                        {defaultVersion === candidate && <span style={DEFAULT_BADGE_STYLE}>default</span>}
                      </Button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default SoftwareVersionsPage;
